import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { adminApi } from "../../api/adminApi";
import type { AuthType, ExternalEndpointConfig, ReportDefinition } from "../../api/types";
import { ReportTabs } from "../../components/ReportTabs";
import { flattenSample } from "../../utils/flattenSample";

const AUTH_TYPES: AuthType[] = [
  "NONE",
  "BEARER_STATIC",
  "BEARER_PASSTHROUGH",
  "API_KEY_HEADER",
  "OAUTH2_CLIENT_CREDENTIALS",
];

function defaultConfig(reportId: string): ExternalEndpointConfig {
  return {
    reportDefinitionId: reportId,
    baseUrl: "",
    dataFetchPath: "",
    httpMethod: "POST",
    authType: "NONE",
    authConfig: {},
    timeoutMs: 15000,
  };
}

export default function EndpointConfig() {
  const { reportId = "" } = useParams();
  const [report, setReport] = useState<ReportDefinition | null>(null);
  const [config, setConfig] = useState<ExternalEndpointConfig>(defaultConfig(reportId));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string; sampleData?: unknown } | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([adminApi.reports.get(reportId), adminApi.reports.getEndpoint(reportId)])
      .then(([r, e]) => {
        setReport(r);
        if (e) setConfig(e);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load endpoint config"))
      .finally(() => setLoading(false));
  }, [reportId]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const saved = await adminApi.reports.saveEndpoint(reportId, config);
      setConfig(saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save endpoint config");
    } finally {
      setSaving(false);
    }
  }

  async function handleTest() {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await adminApi.reports.testEndpoint(reportId, config);
      setTestResult(res);
    } catch (err) {
      setTestResult({ ok: false, message: err instanceof Error ? err.message : "Test failed" });
    } finally {
      setTesting(false);
    }
  }

  function updateAuthField(key: string, value: string) {
    setConfig({ ...config, authConfig: { ...config.authConfig, [key]: value } });
  }

  function authField(key: string): string {
    const value = config.authConfig?.[key];
    return typeof value === "string" ? value : "";
  }

  if (loading) return <p>Loading...</p>;

  return (
    <div>
      <ReportTabs reportId={reportId} title={report?.displayName ?? "Report"} />
      {error && <div className="error-text">{error}</div>}

      <form className="page-panel" onSubmit={handleSubmit}>
        <p className="help-text">
          Where this report's data comes from. Leave unset to require the caller to pass{" "}
          <code>data</code> directly in each request.
        </p>

        <div className="form-grid">
          <div className="form-row">
            <label>Base URL</label>
            <input
              value={config.baseUrl}
              onChange={(e) => setConfig({ ...config, baseUrl: e.target.value })}
              placeholder="https://api.tenant.example.com"
            />
          </div>
          <div className="form-row">
            <label>Data fetch path</label>
            <input
              value={config.dataFetchPath}
              onChange={(e) => setConfig({ ...config, dataFetchPath: e.target.value })}
              placeholder="/reports/member-statement"
            />
          </div>
          <div className="form-row">
            <label>HTTP method</label>
            <select
              value={config.httpMethod}
              onChange={(e) => setConfig({ ...config, httpMethod: e.target.value as any })}
            >
              <option value="GET">GET</option>
              <option value="POST">POST</option>
            </select>
          </div>
          <div className="form-row">
            <label>Timeout (ms)</label>
            <input
              type="number"
              value={config.timeoutMs}
              onChange={(e) => setConfig({ ...config, timeoutMs: Number(e.target.value) })}
            />
          </div>
          <div className="form-row">
            <label>Auth type</label>
            <select
              value={config.authType}
              onChange={(e) => setConfig({ ...config, authType: e.target.value as AuthType })}
            >
              {AUTH_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
        </div>

        {config.authType === "BEARER_STATIC" && (
          <div className="form-row">
            <label>Bearer token</label>
            <input value={authField("token")} onChange={(e) => updateAuthField("token", e.target.value)} />
          </div>
        )}

        {config.authType === "BEARER_PASSTHROUGH" && (
          <p className="help-text">
            The caller supplies the bearer token per request (via <code>authToken</code> in the
            request body), matching the legacy <code>fmJwtToken</code> pattern.
          </p>
        )}

        {config.authType === "API_KEY_HEADER" && (
          <div className="form-grid">
            <div className="form-row">
              <label>Header name</label>
              <input
                value={authField("headerName")}
                onChange={(e) => updateAuthField("headerName", e.target.value)}
                placeholder="X-Api-Key"
              />
            </div>
            <div className="form-row">
              <label>Header value</label>
              <input
                value={authField("headerValue")}
                onChange={(e) => updateAuthField("headerValue", e.target.value)}
              />
            </div>
          </div>
        )}

        {config.authType === "OAUTH2_CLIENT_CREDENTIALS" && (
          <div className="form-grid">
            <div className="form-row">
              <label>Token URL</label>
              <input
                value={authField("tokenUrl")}
                onChange={(e) => updateAuthField("tokenUrl", e.target.value)}
              />
            </div>
            <div className="form-row">
              <label>Client ID</label>
              <input
                value={authField("clientId")}
                onChange={(e) => updateAuthField("clientId", e.target.value)}
              />
            </div>
            <div className="form-row">
              <label>Client secret</label>
              <input
                type="password"
                value={authField("clientSecret")}
                onChange={(e) => updateAuthField("clientSecret", e.target.value)}
              />
            </div>
            <div className="form-row">
              <label>Scope</label>
              <input value={authField("scope")} onChange={(e) => updateAuthField("scope", e.target.value)} />
            </div>
          </div>
        )}

        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </button>
          <button type="button" className="btn-secondary" onClick={handleTest} disabled={testing}>
            {testing ? "Testing..." : "Test connection"}
          </button>
        </div>

        {testResult && (
          <div className={testResult.ok ? "help-text" : "error-text"} style={{ marginTop: 10 }}>
            {testResult.ok ? "Connection OK: " : "Connection failed: "}
            {testResult.message}
          </div>
        )}

        {testResult?.ok && testResult.sampleData !== undefined && (
          <div className="page-panel" style={{ marginTop: 12, background: "var(--color-panel-raised)" }}>
            <div className="help-text" style={{ marginBottom: 8 }}>
              Found {flattenSample(testResult.sampleData).length} field
              {flattenSample(testResult.sampleData).length === 1 ? "" : "s"} in the response.{" "}
              <Link to={`/reports/${reportId}/mapping`}>Go map them →</Link>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {flattenSample(testResult.sampleData)
                .slice(0, 12)
                .map((f) => (
                  <span
                    key={f}
                    className="variable-chip"
                    style={{ display: "inline-block", width: "auto", margin: 0 }}
                  >
                    {f}
                  </span>
                ))}
            </div>
          </div>
        )}
      </form>
    </div>
  );
}
